import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useTheme } from '../contexts/ThemeContext';
import api, { statsApi, textsApi, reviewApi, languagesApi, goalsApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  BookOpen, Plus, RefreshCw, TrendingUp, BookMarked, Target, Clock, 
  ChevronRight, Zap, Flame, Trophy, Lightbulb, BarChart3, 
  Globe, Sparkles, Calendar, FileText, Loader2, Wand2, Lock,
  GripVertical, X, Settings, Eye, EyeOff, GraduationCap, Check, Coins,
  Moon, Sun, Headphones, Layers, Book, CheckCircle, Library
} from 'lucide-react';
import { formatStrength } from '../utils/strengthUtils';
import { CreditBadge, CREDIT_COSTS } from '../components/CreditCost';
import GrammarRecommendations from '../components/GrammarRecommendations';
import GrammarSRSWidget from '../components/GrammarSRSWidget';
import { StreakBadge } from '../components/StreakBadge';
import { StudyPlanWidget } from '../components/StudyPlanWidget';
import { DashboardToolbar } from '../components/dashboard/DashboardToolbar';
import { AddLanguageDialog } from '../components/dashboard/AddLanguageDialog';
import { sortDashboardLanguages } from '../utils/dashboardSorting';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Import centralized language utilities
import { getCountryCode } from '../utils/languageUtils';

// Compact date formatter — returns "Oct 19" style, null if no date
const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Flag image component
const FlagImage = ({ countryCode, className = "w-8 h-6" }) => {
  if (!countryCode) {
    return <Globe className={`${className} text-muted-foreground`} />;
  }
  return (
    <img 
      src={`https://flagcdn.com/w40/${countryCode}.png`}
      srcSet={`https://flagcdn.com/w80/${countryCode}.png 2x`}
      alt={countryCode}
      className={`${className} object-cover rounded`}
      loading="lazy"
    />
  );
};

const getProgressLevel = (known, total, t) => {
  if (!total) return t ? t('progress.startingOut') : 'Starting out';
  const pct = (known / total) * 100;
  if (pct >= 90) return t ? t('progress.master') : 'Master';
  if (pct >= 70) return t ? t('progress.advanced') : 'Advanced';
  if (pct >= 50) return t ? t('progress.intermediate') : 'Intermediate';
  if (pct >= 25) return t ? t('progress.developing') : 'Developing';
  if (pct >= 10) return t ? t('progress.beginningExplorer') : 'Beginning Explorer';
  return t ? t('progress.startingOut') : 'Starting out';
};

// Topics for lesson generation
const LESSON_TOPICS = [
  { id: 'daily_life', label: 'Daily Life', icon: '🏠' },
  { id: 'travel', label: 'Travel', icon: '✈️' },
  { id: 'food', label: 'Food & Cooking', icon: '🍳' },
  { id: 'culture', label: 'Culture & Traditions', icon: '🎭' },
  { id: 'technology', label: 'Technology', icon: '💻' },
  { id: 'nature', label: 'Nature & Environment', icon: '🌿' },
  { id: 'sports', label: 'Sports & Fitness', icon: '⚽' },
  { id: 'business', label: 'Business & Work', icon: '💼' },
  { id: 'history', label: 'History', icon: '📜' },
  { id: 'science', label: 'Science', icon: '🔬' },
  { id: 'entertainment', label: 'Entertainment', icon: '🎬' },
  { id: 'health', label: 'Health & Wellness', icon: '❤️' },
];

// Sortable Widget Wrapper
function SortableWidget({ id, children, onClose, order }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    order: order ?? 0,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {/* Drag handle and close button */}
      <div className="absolute -top-2 -right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 rounded-full bg-background border shadow-sm hover:bg-muted cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </button>
        <button
          onClick={() => onClose(id)}
          className="p-1.5 rounded-full bg-background border shadow-sm hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
          title="Hide widget"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, locale: language, setLocale: setLanguage } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  
  // Data states
  const [languageStats, setLanguageStats] = useState([]);
  const [texts, setTexts] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, has_activity_today: false });
  const [mission, setMission] = useState({ daily_target: 32, words_completed: 0 });
  const [insights, setInsights] = useState(null);
  const [dynamicPlan, setDynamicPlan] = useState(null);
  const [dueCount, setDueCount] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [rotateDailyEnabled, setRotateDailyEnabled] = useState(false);
  const [compactView, setCompactView] = useState(false);
  const [languageSortBy, setLanguageSortBy] = useState('strength');
  const [languageSortDirection, setLanguageSortDirection] = useState('asc');
  const [randomCount, setRandomCount] = useState(null);
  const [randomBusy, setRandomBusy] = useState(false);
  const [randomError, setRandomError] = useState(false);
  
  // New Lesson modal state
  const [showNewLessonModal, setShowNewLessonModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedLessonLanguages, setSelectedLessonLanguages] = useState([]);
  const [generatingLesson, setGeneratingLesson] = useState(false);
  const [lessonILR, setLessonILR] = useState('auto'); // Target ILR level for new lesson
  
  // Widget customization state
  const WIDGET_STORAGE_KEY = 'iling_dashboard_widgets';
  const DEFAULT_WIDGETS = [
    { id: 'languages', name: 'Language Cards', visible: true },
    { id: 'study-plan', name: 'Study Plan', visible: true },
    { id: 'grammar', name: 'Grammar Recommendations', visible: true },
    { id: 'srs', name: 'Grammar Reviews (SRS)', visible: true },
    { id: 'streak', name: 'Daily Streak', visible: true },
    { id: 'mission', name: "Today's Mission", visible: true },
    { id: 'progress', name: 'Word Progress', visible: true },
    { id: 'texts', name: 'Recent Texts', visible: true },
    { id: 'actions', name: 'Quick Actions', visible: true },
    { id: 'goals', name: 'Goal Tracker', visible: true },
    { id: 'current', name: 'Current Text', visible: true },
  ];
  
  const loadWidgets = () => {
    try {
      const saved = localStorage.getItem(WIDGET_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Restore in SAVED order (preserves drag positions)
        const savedWidgets = parsed
          .map(sw => {
            const dw = DEFAULT_WIDGETS.find(d => d.id === sw.id);
            return dw ? { ...dw, visible: sw.visible !== undefined ? sw.visible : dw.visible } : null;
          })
          .filter(Boolean);
        // Append any new widgets added since the user's last save
        const savedIds = new Set(savedWidgets.map(w => w.id));
        const newDefaults = DEFAULT_WIDGETS.filter(dw => !savedIds.has(dw.id));
        return [...savedWidgets, ...newDefaults];
      }
    } catch (e) {}
    return DEFAULT_WIDGETS;
  };
  
  const [widgets, setWidgets] = useState(loadWidgets);
  
  // Save widgets to localStorage
  useEffect(() => {
    localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(widgets));
  }, [widgets]);
  
  // Widget helper functions
  const isWidgetVisible = (id) => widgets.find(w => w.id === id)?.visible ?? true;
  const toggleWidget = (id) => setWidgets(ws => ws.map(w => w.id === id ? { ...w, visible: !w.visible } : w));
  const hideWidget = (id) => setWidgets(ws => ws.map(w => w.id === id ? { ...w, visible: false } : w));
  const resetWidgets = () => setWidgets(DEFAULT_WIDGETS);
  const reorderWidgets = (activeId, overId) => {
    setWidgets(ws => {
      const oldIdx = ws.findIndex(w => w.id === activeId);
      const newIdx = ws.findIndex(w => w.id === overId);
      return arrayMove(ws, oldIdx, newIdx);
    });
  };
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reorderWidgets(active.id, over.id);
    }
  };
  
  // Language texts modal states
  const [languageTextsModal, setLanguageTextsModal] = useState({ open: false, language: null });
  const [languageTexts, setLanguageTexts] = useState([]);
  const [publicTexts, setPublicTexts] = useState([]);
  const [textsTab, setTextsTab] = useState('mine'); // 'mine' or 'public'
  const [loadingLanguageTexts, setLoadingLanguageTexts] = useState(false);
  const [generatingText, setGeneratingText] = useState(false);
  const [textSortBy, setTextSortBy] = useState('recent'); // 'recent', 'title', 'progress'
  const [textCategoryFilter, setTextCategoryFilter] = useState(''); // '' = all
  
  // Generate text options
  const [showGenerateOptions, setShowGenerateOptions] = useState(false);
  const [generateMode, setGenerateMode] = useState(null); // 'weak' or 'topic'
  const [topicInput, setTopicInput] = useState('');
  
  // Batch generation modal
  const [batchGenerateModal, setBatchGenerateModal] = useState(false);
  const [batchTopicInput, setBatchTopicInput] = useState('');
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  
  // Proficiency change modal
  const [proficiencyModal, setProficiencyModal] = useState({ open: false, language: null, currentProficiency: 'beginner' });
  const [updatingProficiency, setUpdatingProficiency] = useState(false);
  
  // Language removal modal
  const [removeModal, setRemoveModal] = useState({ open: false, language: null, step: 'confirm' }); // step: 'confirm' | 'progress'
  const [removingLanguage, setRemovingLanguage] = useState(false);
  
  // Add language modal
  const [addLanguageModal, setAddLanguageModal] = useState(false);
  const [allAvailableLanguages, setAllAvailableLanguages] = useState([]);
  const [addingLanguage, setAddingLanguage] = useState(false);
  const [loadingAvailableLanguages, setLoadingAvailableLanguages] = useState(false);
  const [availableLanguagesError, setAvailableLanguagesError] = useState(false);
  
  // Hidden languages
  const [hiddenLanguages, setHiddenLanguages] = useState([]);
  
  // Check if user can generate texts (admin or tier 3)
  const canGenerateTexts = user?.is_admin || (user?.tier && user.tier >= 3);
  // Check if user can import (admin or tier 2+)
  const canImport = user?.is_admin || (user?.tier && user.tier >= 2);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [langStatsRes, textsRes, dueRes, streakRes, missionRes, insightsRes, creditsRes, planRes, hiddenRes] = await Promise.all([
          statsApi.getLanguageStats().catch(() => ({ data: [] })),
          textsApi.getAll({ limit: 5 }).catch(() => ({ data: [] })),
          reviewApi.getQueueCount().catch(() => ({ data: { count: 0 } })),
          goalsApi.getStreak().catch(() => ({ data: { current_streak: 0 } })),
          goalsApi.getDailyMission().catch(() => ({ data: { daily_target: 32, words_completed: 0 } })),
          goalsApi.getInsights().catch(() => ({ data: null })),
          api.get('/credits/balance').catch(() => ({ data: { balance: 0 } })),
          goalsApi.getDynamicPlan().catch(() => ({ data: null })),
          languagesApi.getHiddenLanguages().catch(() => ({ data: [] })),
        ]);
        
        // Filter out hidden languages from dashboard display
        const hiddenIds = new Set((hiddenRes.data || []).map(h => h.language_id));
        const visibleStats = langStatsRes.data.filter(ls => !hiddenIds.has(ls.language?.language_id));
        
        setLanguageStats(visibleStats);
        setHiddenLanguages(hiddenRes.data || []);
        setTexts(textsRes.data);
        setDueCount(dueRes.data.count);
        setStreak(streakRes.data);
        setMission(missionRes.data);
        setInsights(insightsRes.data);
        setCredits(creditsRes.data?.balance || 0);
        setDynamicPlan(planRes.data);
        
        // Set first language as selected
        if (langStatsRes.data.length > 0) {
          setSelectedLanguage(langStatsRes.data[0].language?.language_id);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    setRandomCount(null);
    setRandomError(false);
    textsApi.getRandomLesson().then(({ data }) => {
      if (!cancelled) setRandomCount(data.total);
    }).catch(() => {
      if (!cancelled) setRandomError(true);
    });
    return () => { cancelled = true; };
  }, [loading, languageStats]);

  const handleRandomLesson = async () => {
    if (randomBusy || (randomCount !== null && randomCount < 2)) return;
    setRandomBusy(true);
    try {
      const { data } = await textsApi.getRandomLesson();
      setRandomCount(data.total);
      setRandomError(false);
      if (data.total < 2) {
        toast.info(t('dashboard.randomNeedsTexts'));
      } else if (data.text_id) {
        navigate(`/reader/${data.text_id}`);
      } else {
        toast.error(t('dashboard.randomUnavailable'));
      }
    } catch {
      setRandomError(true);
      toast.error(t('dashboard.randomUnavailable'));
    } finally {
      setRandomBusy(false);
    }
  };

  // Check in streak when user has activity
  useEffect(() => {
    if (!loading && !streak.has_activity_today && (mission.words_completed > 0 || dueCount > 0)) {
      goalsApi.checkInStreak().catch(console.error);
    }
  }, [loading, streak.has_activity_today, mission.words_completed, dueCount]);

  // Handle language card click - open texts modal
  const handleLanguageClick = async (language) => {
    setSelectedLanguage(language.language_id);
    setLanguageTextsModal({ open: true, language });
    setLoadingLanguageTexts(true);
    setTextsTab('mine');
    
    try {
      const [myRes, pubRes] = await Promise.all([
        textsApi.getAll({ language_id: language.language_id, limit: 50 }),
        textsApi.getPublicTexts(language.language_id).catch(() => ({ data: { texts: [] } })),
      ]);
      setLanguageTexts(myRes.data);
      const pubData = pubRes.data;
      setPublicTexts(Array.isArray(pubData) ? pubData : (pubData?.texts || []));
    } catch (err) {
      console.error('Failed to load texts:', err);
      toast.error('Failed to load texts for this language');
    } finally {
      setLoadingLanguageTexts(false);
    }
  };

  // Handle generate new text from vocabulary
  const handleGenerateText = async (mode) => {
    if (!languageTextsModal.language) return;
    
    setGeneratingText(true);
    try {
      let res;
      if (mode === 'topic' && topicInput.trim()) {
        res = await textsApi.generateFromTopic(languageTextsModal.language.language_id, topicInput.trim(), 250);
      } else {
        res = await textsApi.generateFromVocabulary(languageTextsModal.language.language_id, 250);
      }
      toast.success(res.data.message || 'Text generated successfully!');
      setLanguageTextsModal({ open: false, language: null });
      setShowGenerateOptions(false);
      setGenerateMode(null);
      setTopicInput('');
      navigate(`/reader/${res.data.text_id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to generate text';
      toast.error(errorMsg);
    } finally {
      setGeneratingText(false);
    }
  };

  // Reset generate options when modal closes
  const handleCloseModal = (open) => {
    setLanguageTextsModal({ open, language: open ? languageTextsModal.language : null });
    if (!open) {
      setShowGenerateOptions(false);
      setGenerateMode(null);
      setTopicInput('');
    }
  };

  // Handle batch text generation for all languages
  const handleBatchGenerate = async () => {
    if (!batchTopicInput.trim()) return;
    
    // Get all user's language IDs
    const languageIds = languageStats.map(ls => ls.language?.language_id).filter(Boolean);
    
    if (languageIds.length === 0) {
      toast.error('No languages selected. Add languages first.');
      return;
    }
    
    setBatchGenerating(true);
    setBatchResults(null);
    
    try {
      const res = await textsApi.generateBatchFromTopic(languageIds, batchTopicInput.trim(), 250);
      setBatchResults(res.data);
      
      if (res.data.total_generated > 0) {
        toast.success(`Generated ${res.data.total_generated} text(s) about "${batchTopicInput.trim()}"!`);
      }
      if (res.data.total_errors > 0) {
        toast.error(`${res.data.total_errors} text(s) failed to generate`);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to generate texts';
      toast.error(errorMsg);
    } finally {
      setBatchGenerating(false);
    }
  };

  // Close batch modal and reset
  const handleCloseBatchModal = () => {
    setBatchGenerateModal(false);
    setBatchTopicInput('');
    setBatchResults(null);
  };

  // Handle New Lesson generation
  const handleGenerateNewLesson = async () => {
    if (!selectedTopic) {
      toast.error('Please select a topic');
      return;
    }
    
    if (selectedLessonLanguages.length === 0) {
      toast.error('Please select at least one language');
      return;
    }
    
    setGeneratingLesson(true);
    
    try {
      const languageConfigs = selectedLessonLanguages.map(langId => {
        const langStat = languageStats.find(ls => ls.language?.language_id === langId);
        // Use selected ILR if set, otherwise fallback to saved proficiency
        const proficiency = lessonILR !== 'auto' ? lessonILR : (langStat?.language?.proficiency || 'beginner');
        return {
          language_id: langId,
          proficiency: proficiency
        };
      });
      
      const topicLabel = LESSON_TOPICS.find(t => t.id === selectedTopic)?.label || selectedTopic;
      
      const res = await axios.post(`${BACKEND_URL}/api/texts/generate-batch-from-topic`, {
        topic: topicLabel,
        languages: languageConfigs,
        word_count: 200
      }, { withCredentials: true });
      
      if (res.data.total_generated > 0) {
        toast.success(`Generated ${res.data.total_generated} new lesson(s) about "${topicLabel}"!`);
        // Refresh texts list
        try {
          const refreshRes = await textsApi.getAll({ limit: 5 });
          setTexts(refreshRes.data);
        } catch (e) { /* ignore refresh errors */ }
        // Navigate to the first generated text
        if (res.data.generated_texts?.length > 0) {
          const firstText = res.data.generated_texts[0];
          navigate(`/reader/${firstText.text_id}`);
        }
      }
      
      setShowNewLessonModal(false);
      setSelectedTopic(null);
      setSelectedLessonLanguages([]);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to generate lesson';
      toast.error(errorMsg);
    } finally {
      setGeneratingLesson(false);
    }
  };

  // Toggle language selection for new lesson
  const toggleLessonLanguage = (langId) => {
    setSelectedLessonLanguages(prev => 
      prev.includes(langId) 
        ? prev.filter(id => id !== langId)
        : [...prev, langId]
    );
  };

  // Open proficiency modal for a language
  const handleOpenProficiencyModal = (language, currentProficiency) => {
    setProficiencyModal({ 
      open: true, 
      language, 
      currentProficiency: currentProficiency || 'beginner' 
    });
  };

  // Update proficiency level
  const handleUpdateProficiency = async (newProficiency) => {
    if (!proficiencyModal.language) return;
    
    setUpdatingProficiency(true);
    try {
      await languagesApi.updateProficiency(proficiencyModal.language.language_id, newProficiency);
      toast.success(`Proficiency updated to ${newProficiency}`);
      
      // Update local state
      setLanguageStats(prev => prev.map(ls => 
        ls.language?.language_id === proficiencyModal.language.language_id
          ? { ...ls, language: { ...ls.language, proficiency: newProficiency } }
          : ls
      ));
      
      setProficiencyModal({ open: false, language: null, currentProficiency: 'beginner' });
    } catch (err) {
      toast.error('Failed to update proficiency');
    } finally {
      setUpdatingProficiency(false);
    }
  };

  // Handle language removal flow
  const handleRemoveLanguage = (lang, e) => {
    e.stopPropagation();
    setRemoveModal({ open: true, language: lang, step: 'confirm' });
  };

  const confirmRemoveLanguage = async (deleteProgress) => {
    const lang = removeModal.language;
    if (!lang) return;
    setRemovingLanguage(true);
    try {
      if (deleteProgress) {
        // Full delete: remove language + all progress
        await languagesApi.removeUserLanguage(lang.language_id);
        toast.success(`${lang.name} and all progress removed`);
      } else {
        // Hide only: keep progress, just hide from dashboard
        await languagesApi.hideUserLanguage(lang.language_id, true);
        toast.success(`${lang.name} hidden from dashboard. Progress saved.`);
      }
      // Remove from local state
      setLanguageStats(prev => prev.filter(ls => ls.language?.language_id !== lang.language_id));
      if (selectedLanguage === lang.language_id) {
        setSelectedLanguage(null);
      }
      setRemoveModal({ open: false, language: null, step: 'confirm' });
      // Refresh hidden languages
      languagesApi.getHiddenLanguages().then(r => setHiddenLanguages(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error('Failed to remove language');
    } finally {
      setRemovingLanguage(false);
    }
  };

  // Restore a hidden language
  const handleRestoreLanguage = async (langId) => {
    try {
      await languagesApi.hideUserLanguage(langId, false);
      toast.success('Language restored');
      // Refresh dashboard data
      const [langStatsRes, hiddenRes] = await Promise.all([
        statsApi.getLanguageStats(),
        languagesApi.getHiddenLanguages().catch(() => ({ data: [] })),
      ]);
      const hiddenIds = new Set((hiddenRes.data || []).map(h => h.language_id));
      setLanguageStats(langStatsRes.data.filter(ls => !hiddenIds.has(ls.language?.language_id)));
      setHiddenLanguages(hiddenRes.data || []);
      return true;
    } catch (err) {
      toast.error('Failed to restore language');
      return false;
    }
  };

  // Add language modal
  const openAddLanguageModal = async () => {
    setAddLanguageModal(true);
    setLoadingAvailableLanguages(true);
    setAvailableLanguagesError(false);
    try {
      const res = await languagesApi.getAll();
      setAllAvailableLanguages(res.data || []);
    } catch {
      setAvailableLanguagesError(true);
    } finally {
      setLoadingAvailableLanguages(false);
    }
  };

  const handleAddLanguage = async (langId) => {
    setAddingLanguage(true);
    try {
      await languagesApi.addUserLanguage({ language_id: langId, role: 'studying' });
      toast.success('Language added!');
      setAddLanguageModal(false);
      // Refresh
      const [langStatsRes, hiddenRes] = await Promise.all([
        statsApi.getLanguageStats(),
        languagesApi.getHiddenLanguages().catch(() => ({ data: [] })),
      ]);
      const hiddenIds = new Set((hiddenRes.data || []).map(h => h.language_id));
      setLanguageStats(langStatsRes.data.filter(ls => !hiddenIds.has(ls.language?.language_id)));
      setHiddenLanguages(hiddenRes.data || []);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add language';
      toast.error(msg);
    } finally {
      setAddingLanguage(false);
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const selectedLangData = languageStats.find(ls => ls.language?.language_id === selectedLanguage);
  const sortedLanguageStats = sortDashboardLanguages(languageStats, languageSortBy, languageSortDirection, language);

  // ── Cross-column drag support ─────────────────────────────────────────────
  const GRID_WIDGET_IDS = ['grammar','srs','streak','mission','progress','texts','actions','study-plan','goals','current'];
  const visibleGridWidgets = widgets.filter(w => w.visible && GRID_WIDGET_IDS.includes(w.id));

  const renderWidgetContent = (id) => {
    if (id === 'grammar') return <GrammarRecommendations type="general" limit={3} showDismiss={false} />;
    if (id === 'srs') return <GrammarSRSWidget />;
    if (id === 'study-plan') return <StudyPlanWidget />;
    if (id === 'streak') return (
      <Card className="overflow-hidden" data-testid="streak-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500 animate-pulse" />
              <span className="font-semibold">{t('dashboard.dailyStreak')}</span>
            </div>
            <span className="text-sm text-muted-foreground">{streak.current_streak} {t('common.days')}</span>
          </div>
          <div className="flex justify-center py-4">
            <div className="relative w-36 h-36">
              <div className="absolute inset-0 rounded-full blur-xl opacity-30 transition-opacity duration-1000" style={{ background: streak.current_streak >= 30 ? 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' : streak.current_streak >= 7 ? 'radial-gradient(circle, #f97316 0%, transparent 70%)' : 'radial-gradient(circle, #ef4444 0%, transparent 70%)', opacity: streak.has_activity_today ? 0.4 : 0.15 }} />
              {streak.current_streak >= 7 && streak.has_activity_today && (<>{[...Array(Math.min(streak.current_streak >= 30 ? 6 : 3, 6))].map((_, i) => (<div key={i} className="absolute w-1.5 h-1.5 rounded-full" style={{ background: ['#f97316','#eab308','#f59e0b','#ef4444','#fbbf24','#fb923c'][i%6], left: `${50+42*Math.cos(((Date.now()/2000)+i*1.05)*Math.PI)}%`, top: `${50+42*Math.sin(((Date.now()/2000)+i*1.05)*Math.PI)}%`, animation: `float-particle ${2+i*0.3}s ease-in-out infinite alternate`, animationDelay: `${i*0.4}s`, opacity:0.7 }} />))}</>)}
              <svg className="w-full h-full transform -rotate-90 relative z-10" viewBox="0 0 128 128">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="6" fill="none" className="text-muted/20" />
                <circle cx="64" cy="64" r="56" stroke="url(#streakGradient)" strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={`${Math.min(streak.current_streak*5,100)*3.51} 351`} className="transition-all duration-1000 ease-out" style={{ filter: streak.has_activity_today ? 'drop-shadow(0 0 6px rgba(249, 115, 22, 0.6))' : 'none' }} />
                <defs><linearGradient id="streakGradient" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={streak.current_streak>=30?'#eab308':'#f97316'} /><stop offset="50%" stopColor="#f59e0b" /><stop offset="100%" stopColor={streak.current_streak>=100?'#fbbf24':'#eab308'} /></linearGradient></defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center z-20"><div className="text-center"><span className="text-4xl font-bold tabular-nums" style={{ background: streak.current_streak>=30?'linear-gradient(135deg, #f59e0b, #fbbf24)':streak.current_streak>=7?'linear-gradient(135deg, #f97316, #eab308)':undefined, WebkitBackgroundClip: streak.current_streak>=7?'text':undefined, WebkitTextFillColor: streak.current_streak>=7?'transparent':undefined }} data-testid="streak-count">{streak.current_streak}</span><Flame className={`h-4 w-4 mx-auto mt-0.5 ${streak.has_activity_today?'text-orange-400 animate-bounce':'text-muted-foreground'}`} /></div></div>
            </div>
          </div>
          <div className="flex justify-center gap-2 pb-2">{[{days:7,label:'7d',icon:'🔥',color:'bg-orange-500/20 text-orange-400 border-orange-500/30'},{days:30,label:'30d',icon:'⚡',color:'bg-amber-500/20 text-amber-400 border-amber-500/30'},{days:100,label:'100d',icon:'💎',color:'bg-blue-500/20 text-blue-400 border-blue-500/30'},{days:365,label:'1yr',icon:'👑',color:'bg-purple-500/20 text-purple-400 border-purple-500/30'}].map(m=>{const reached=streak.current_streak>=m.days||(streak.longest_streak||0)>=m.days;return(<div key={m.days} className={`px-2 py-1 rounded-full text-[10px] font-semibold border transition-all ${reached?m.color:'bg-muted/30 text-muted-foreground/40 border-muted/20'}`} title={reached?`${m.label} milestone reached!`:`${m.days-streak.current_streak} days to ${m.label}`} data-testid={`milestone-${m.days}`}>{m.icon} {m.label}</div>);})}</div>
          <p className="text-center text-sm text-muted-foreground">{streak.has_activity_today?(streak.current_streak>=30?'🔥 Blazing! You\'re unstoppable.':streak.current_streak>=7?'✨ Great momentum! Keep it up.':t('dashboard.streakKeepGoing')):t('dashboard.streakReviewToday')}</p>
        </CardContent>
      </Card>
    );
    if (id === 'mission') return (
      <Card data-testid="mission-card">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Target className="h-5 w-5 text-primary" /><span className="font-semibold">Today's Plan</span></div>
            {dynamicPlan && <span className="text-xs text-muted-foreground">~{dynamicPlan.total_estimate_min} min total</span>}
          </div>
          {dynamicPlan?.tasks?.length > 0 ? (
            <div className="space-y-2">{dynamicPlan.tasks.map((task,idx)=>{const iconMap={'refresh-cw':<RefreshCw className="h-4 w-4"/>,'layers':<Layers className="h-4 w-4"/>,'book-open':<BookOpen className="h-4 w-4"/>,'flame':<Flame className="h-4 w-4"/>,'headphones':<Headphones className="h-4 w-4"/>,'book':<Book className="h-4 w-4"/>};const colorMap={review:'text-red-400 bg-red-500/10 border-red-500/20',story:'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',forge:'text-amber-400 bg-amber-500/10 border-amber-500/20',listen:'text-violet-400 bg-violet-500/10 border-violet-500/20',read:'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'};const colors=colorMap[task.type]||'text-slate-400 bg-slate-500/10 border-slate-500/20';return(<Link key={idx} to={task.action_url}><div className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all hover:scale-[1.01] cursor-pointer ${colors} ${task.done?'opacity-60':''}`} data-testid={`plan-task-${task.type}`}><div className="flex-shrink-0">{task.done?<CheckCircle className="h-4 w-4 text-green-500"/>:(iconMap[task.icon]||<Target className="h-4 w-4"/>)}</div><div className="flex-1 min-w-0"><p className={`text-sm font-medium truncate ${task.done?'line-through opacity-70':''}`}>{task.title}</p><p className="text-[10px] opacity-70 truncate">{task.description}</p></div><div className="flex items-center gap-2 flex-shrink-0">{task.done?<span className="text-[10px] text-green-500 font-medium">Done</span>:<span className="text-[10px] opacity-60">{task.estimate_min}m</span>}<ChevronRight className="h-3 w-3 opacity-40"/></div></div></Link>);})}</div>
          ) : (
            <div className="space-y-4"><div className="flex items-center justify-between"><div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-yellow-500"/><span className="text-sm">{t('dashboard.wordsToReview')}</span></div><span className="font-semibold">{mission.words_completed||0} / {mission.daily_target||32}</span></div><Progress value={mission.progress_percent||0} className="h-3"/>{dueCount>0&&<Link to="/review"><Button className="w-full gap-2" data-testid="start-review-btn"><RefreshCw className="h-4 w-4"/>{t('dashboard.startReview')} ({dueCount} {t('common.due')})</Button></Link>}</div>
          )}
        </CardContent>
      </Card>
    );
    if (id === 'progress') return (
      <Card data-testid="word-progress-card">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-4"><BarChart3 className="h-5 w-5 text-primary"/><span className="font-semibold">{t('dashboard.wordProgress')}</span></div>
          {insights && (<div className="space-y-3"><div className="flex h-4 rounded-full overflow-hidden bg-muted/30">{[{key:'weak',color:'bg-red-500'},{key:'medium',color:'bg-orange-500'},{key:'strong',color:'bg-yellow-500'},{key:'mastered',color:'bg-green-500'}].map(({key,color})=>{const count=insights.strength_distribution?.[key]||0;const total=Object.values(insights.strength_distribution||{}).reduce((a,b)=>a+b,0);const pct=total>0?(count/total)*100:0;return pct>0?<div key={key} className={`${color} transition-all`} style={{width:`${pct}%`}}/>:null;})}</div><div className="grid grid-cols-4 gap-2 text-xs">{[{key:'weak',color:'text-red-500',labelKey:'dashboard.weak'},{key:'medium',color:'text-orange-500',labelKey:'dashboard.medium'},{key:'strong',color:'text-yellow-500',labelKey:'dashboard.strong'},{key:'mastered',color:'text-green-500',labelKey:'dashboard.mastered'}].map(({key,color,labelKey})=>(<div key={key} className="text-center"><div className={`font-semibold ${color}`}>{insights.strength_distribution?.[key]||0}</div><div className="text-muted-foreground">{t(labelKey)}</div></div>))}</div></div>)}
        </CardContent>
      </Card>
    );
    if (id === 'texts') return (
      <Card className="h-fit" data-testid="recent-texts-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-lg">{t('dashboard.recentTexts')}</CardTitle><Link to="/texts"><Button variant="ghost" size="sm" className="gap-1">{t('dashboard.viewAll')} <ChevronRight className="h-4 w-4"/></Button></Link></CardHeader>
        <CardContent>{texts.length===0?(<div className="text-center py-8"><BookOpen className="h-12 w-12 mx-auto text-muted-foreground/50"/><p className="mt-4 text-muted-foreground">{t('dashboard.noTexts')}</p><Link to="/import"><Button className="mt-4 gap-2" variant="outline" data-testid="import-first-text-btn"><Plus className="h-4 w-4"/>{t('dashboard.importFirst')}</Button></Link></div>):(<div className="space-y-3">{texts.map(text=>(<Link key={text.text_id} to={`/reader/${text.text_id}`} className="block"><div className="p-3 rounded-lg border hover:bg-muted/50 transition-colors" data-testid={`text-item-${text.text_id}`}><div className="flex items-center justify-between"><div className="flex-1 min-w-0"><p className={`font-medium truncate ${/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text.title)?'font-arabic':''}`} dir="auto">{text.title}</p><p className="text-sm text-muted-foreground">{text.word_count||0} {t('common.words')} • {text.unique_words||0} unique</p></div><ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0"/></div></div></Link>))}</div>)}</CardContent>
      </Card>
    );
    if (id === 'actions') return (
      <div className="grid grid-cols-2 gap-3"><Link to="/import"><Button variant="outline" className="w-full gap-2 h-auto py-4" data-testid="quick-import-btn"><Plus className="h-5 w-5"/><span>{t('dashboard.importText')}</span></Button></Link><Link to="/vocabulary"><Button variant="outline" className="w-full gap-2 h-auto py-4" data-testid="vocabulary-btn"><BookMarked className="h-5 w-5"/><span>{t('nav.vocabulary')}</span></Button></Link></div>
    );
    if (id === 'goals') return languageStats.length > 0 ? (
      <Card data-testid="goal-tracker-card">
        <CardHeader className="pb-2"><div className="flex items-center gap-2"><Target className="h-5 w-5 text-primary"/><CardTitle className="text-lg">{t('dashboard.goalTracker')}</CardTitle></div></CardHeader>
        <CardContent><select value={selectedLanguage} onChange={(e)=>setSelectedLanguage(e.target.value)} className="w-full mb-4 p-2 rounded-lg bg-muted border border-border text-sm" data-testid="goal-language-select">{languageStats.map(ls=>(<option key={ls.language?.language_id} value={ls.language?.language_id}>{ls.language?.name}</option>))}</select>{languageStats.filter(ls=>ls.language?.language_id===selectedLanguage).map(ls=>{const known=ls.known_count||0;const target=2000;const pct=Math.round((known/target)*100);return(<div key={ls.language?.language_id}><div className="space-y-4"><div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20"><p className="text-sm text-muted-foreground mb-1">You will reach</p><p className="text-2xl font-bold text-green-500">{target.toLocaleString()} words</p><p className="text-sm text-muted-foreground">by {insights?.projected_date||'setting a goal'}</p></div><div className="space-y-2"><div className="flex justify-between text-sm"><span>{known.toLocaleString()} / {target.toLocaleString()} words</span><span className="text-muted-foreground">{pct}%</span></div><Progress value={pct} className="h-2"/><p className="text-xs text-muted-foreground">Daily goal: {mission.daily_target||32} words</p></div></div></div>);})}</CardContent>
      </Card>
    ) : null;
    if (id === 'current') return texts.length > 0 ? (
      <Card data-testid="current-text-card">
        <CardContent className="pt-6"><div className="flex items-center gap-2 mb-4"><BookOpen className="h-5 w-5 text-primary"/><span className="font-semibold">{t('dashboard.currentText')}</span></div><Link to={`/reader/${texts[0].text_id}`} className="block"><div className="p-4 rounded-xl border hover:bg-muted/50 transition-colors"><p className="font-medium truncate mb-2">{texts[0].title}</p><div className="flex items-center justify-between text-sm text-muted-foreground mb-3"><span>{t('bible.progress')}: {Math.round(((texts[0].known_words||0)/(texts[0].unique_words||1))*100)}%</span></div><Progress value={((texts[0].known_words||0)/(texts[0].unique_words||1))*100} className="h-2"/><div className="flex justify-between text-xs text-muted-foreground mt-2"><span>{t('common.words')}: {texts[0].word_count}</span><span>{t('common.unique')}: {texts[0].unique_words}</span></div></div></Link></CardContent>
      </Card>
    ) : null;
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Welcome Section with Customize Button */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-2 min-w-0">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight break-words">
            {t('dashboard.welcomeBack')}, {user?.name?.split(' ')[0] || 'Learner'} 👋
          </h1>
          <p className="text-muted-foreground">
            {t('dashboard.languageQuestion')}
          </p>
        </div>
        
        {/* Top-right controls: Credits | Language | Streak | Theme | Settings */}
        <div className="flex shrink-0 items-center gap-1 sm:gap-2 self-end xl:self-start" data-testid="dashboard-top-controls">
          {/* Credit Meter */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/buy-credits')}
            className="gap-1.5 px-1.5 sm:px-3 text-amber-400 hover:text-amber-300"
            data-testid="dashboard-credit-meter"
          >
            <Zap className="h-4 w-4" />
            <span className="font-semibold">{credits.toLocaleString()}</span>
          </Button>

          {/* Interface Language */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 px-1.5 sm:px-3" data-testid="dashboard-lang-btn">
                <Globe className="h-4 w-4 text-amber-400" />
                <span className="text-xs uppercase">{language}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {[
                { code: 'en',    label: 'English' },
                { code: 'pt-BR', label: 'Português' },
                { code: 'es',    label: 'Español' },
                { code: 'fr',    label: 'Français' },
                { code: 'ru',    label: 'Русский' },
                { code: 'zh',    label: '中文' },
                { code: 'fa',    label: 'فارسی' },
                { code: 'ar',    label: 'العربية' },
              ].map(lang => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`cursor-pointer ${language === lang.code ? 'bg-amber-500/10 text-amber-400' : ''}`}
                >
                  {lang.label}
                  {language === lang.code && <span className="ml-auto">✓</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <StreakBadge streak={streak} />

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-8 w-8 shrink-0"
            data-testid="dashboard-theme-toggle"
            aria-label={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Widget Settings */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2 w-8 shrink-0 px-0 sm:w-auto sm:px-3" data-testid="widget-settings-btn" aria-label={t('common.settings')} title={t('common.settings')}>
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">{t('common.settings')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Dashboard Widgets</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {widgets.map(widget => (
                <DropdownMenuCheckboxItem
                  key={widget.id}
                  checked={widget.visible}
                  onCheckedChange={() => toggleWidget(widget.id)}
                  data-testid={`widget-toggle-${widget.id}`}
                >
                  <div className="flex items-center gap-2">
                    {widget.visible ? (
                      <Eye className="h-3 w-3 text-green-500" />
                    ) : (
                      <EyeOff className="h-3 w-3 text-muted-foreground" />
                    )}
                    {widget.name}
                  </div>
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem onSelect={resetWidgets} className="text-muted-foreground">
                {t('common.resetToDefault')}
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <DashboardToolbar
        canGenerate={canGenerateTexts}
        onNewLesson={() => setShowNewLessonModal(true)}
        onGenerateAll={() => setBatchGenerateModal(true)}
        onAddLanguage={openAddLanguageModal}
        sortBy={languageSortBy}
        onSortBy={setLanguageSortBy}
        direction={languageSortDirection}
        onDirection={() => setLanguageSortDirection(value => value === 'asc' ? 'desc' : 'asc')}
        compact={compactView}
        onCompact={() => setCompactView(value => !value)}
        randomCount={randomCount}
        randomBusy={randomBusy}
        randomError={randomError}
        onRandom={handleRandomLesson}
      />

      {/* Language Focus Cards */}
      {compactView ? (
        /* ── Compact list — shares the dashboard sort order ── */
        <div className="flex flex-col gap-2" data-testid="language-focus-compact">
          {sortedLanguageStats.map((ls, idx) => {
              const lang = ls.language;
              const isSelected = selectedLanguage === lang?.language_id;
              const known = ls.known_count || 0;
              const familiar = ls.familiar_count || 0;
              const learning = ls.learning_count || 0;
              const total = ls.total_unique_words || ls.total_vocabulary || 0;
              const tracked = known + familiar + learning;

              return (
                <div
                  key={lang?.language_id || idx}
                  className={`rounded-lg cursor-pointer transition-all duration-150 ${
                    isSelected ? 'ring-1 ring-primary/60' : 'hover:ring-1 hover:ring-border'
                  }`}
                  style={{
                    background: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border) / 0.4)',
                    padding: '10px 14px',
                  }}
                  onClick={() => handleLanguageClick(lang)}
                  data-testid={`compact-lang-${lang?.code}`}
                >
                  {/* Top row: flag + name + progress numbers */}
                  <div className="flex items-center gap-3">
                    <FlagImage countryCode={getCountryCode(lang)} className="w-8 h-6 rounded-sm flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between">
                        <div>
                          <span className="font-semibold text-sm">{lang?.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{getProgressLevel(tracked, total, t)}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="flex items-baseline gap-1">
                            <span className="text-lg font-bold leading-none">{tracked.toLocaleString()}</span>
                            <span className="text-xs text-muted-foreground">/ {total.toLocaleString()}</span>
                          </div>
                          <button
                            onClick={(e) => handleRemoveLanguage(lang, e)}
                            className="p-1 rounded-full opacity-40 hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
                            title={`Remove ${lang?.name}`}
                            data-testid={`compact-remove-lang-${lang?.code}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      {/* Progress bar: learning (red) | familiar (yellow) | known (green) */}
                      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden flex mt-1.5">
                        {total > 0 && learning > 0 && (
                          <div style={{ width: `${(learning / total) * 100}%`, backgroundColor: '#EF4444' }} className="h-full" />
                        )}
                        {total > 0 && familiar > 0 && (
                          <div style={{ width: `${(familiar / total) * 100}%`, backgroundColor: '#EAB308' }} className="h-full" />
                        )}
                        {total > 0 && known > 0 && (
                          <div style={{ width: `${(known / total) * 100}%`, backgroundColor: '#22C55E' }} className="h-full" />
                        )}
                      </div>
                      {/* Bottom row */}
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-green-500">+{ls.text_count || 0} {t('common.texts')}</span>
                        <span className="text-xs text-muted-foreground">{ls.due_today || 0} {t('common.due')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
      /* ── Grid view (original) ── */
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sortedLanguageStats.map((ls, idx) => {
          const lang = ls.language;
          const isSelected = selectedLanguage === lang?.language_id;
          const known = ls.known_count || 0;
          const familiar = ls.familiar_count || 0;
          const learning = ls.learning_count || 0;
          const total = ls.total_unique_words || ls.total_vocabulary || 0;
          const tracked = known + familiar + learning;
          
          return (
            <Card 
              key={lang?.language_id || idx}
              className={`cursor-pointer transition-all duration-200 overflow-hidden relative group ${
                isSelected ? 'ring-2 ring-primary shadow-lg' : 'hover:shadow-md'
              }`}
              onClick={() => handleLanguageClick(lang)}
              data-testid={`language-card-${lang?.code}`}
            >
              {/* Subtle gradient background */}
              <div className={`absolute inset-0 opacity-10 ${
                idx === 0 ? 'bg-gradient-to-br from-orange-500 to-red-600' :
                idx === 1 ? 'bg-gradient-to-br from-purple-500 to-pink-600' :
                'bg-gradient-to-br from-blue-500 to-cyan-600'
              }`} />
              
              <CardContent className="pt-5 pb-4 relative">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <FlagImage countryCode={getCountryCode(lang)} className="w-10 h-7" />
                    <div>
                      <h3 className="font-semibold text-lg">{lang?.name}</h3>
                      <p className="text-xs text-muted-foreground">{getProgressLevel(tracked, total, t)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    )}
                    <button
                      onClick={(e) => handleRemoveLanguage(lang, e)}
                      className="p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
                      title={`Remove ${lang?.name}`}
                      data-testid={`remove-lang-${lang?.code}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="text-2xl font-bold">{tracked.toLocaleString()}</span>
                    <span className="text-sm text-muted-foreground whitespace-nowrap">/ {total.toLocaleString()} {t('common.words')}</span>
                  </div>
                  {/* Multi-segment progress bar: learning (red) | familiar (yellow) | known (green) */}
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden flex" data-testid="mastery-progress-bar">
                    {total > 0 && learning > 0 && (
                      <div style={{ width: `${(learning / total) * 100}%`, backgroundColor: '#EF4444' }} className="h-full" title={`Learning: ${learning}`} />
                    )}
                    {total > 0 && familiar > 0 && (
                      <div style={{ width: `${(familiar / total) * 100}%`, backgroundColor: '#EAB308' }} className="h-full" title={`Familiar: ${familiar}`} />
                    )}
                    {total > 0 && known > 0 && (
                      <div style={{ width: `${(known / total) * 100}%`, backgroundColor: '#22C55E' }} className="h-full" title={`Known: ${known}`} />
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span className="text-green-500">+{ls.text_count || 0} {t('common.texts')}</span>
                    <span>{ls.due_today || 0} {t('common.dueToday')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {/* Rotate Daily Toggle */}
      {languageStats.length > 1 && isWidgetVisible('languages') && (
        <div className="flex items-center gap-3">
          <Switch 
            checked={rotateDailyEnabled} 
            onCheckedChange={setRotateDailyEnabled}
            data-testid="rotate-daily-switch"
          />
          <span className="text-sm text-muted-foreground">{t('common.rotateDailyLabel')}</span>
        </div>
      )}

      {/* Main Content Grid with DnD — cross-column drag supported */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleGridWidgets.map(w => w.id)} strategy={rectSortingStrategy}>
          <div className="grid lg:grid-cols-3 gap-6 items-start">
            {/* Column 0 — widgets at positions 0, 3, 6, … */}
            <div className="flex flex-col gap-6">
              {visibleGridWidgets.filter((_, i) => i % 3 === 0).map(w => (
                <SortableWidget key={w.id} id={w.id} onClose={hideWidget}>
                  {renderWidgetContent(w.id)}
                </SortableWidget>
              ))}
            </div>

            {/* Column 1 — widgets at positions 1, 4, 7, … */}
            <div className="flex flex-col gap-6">
              {visibleGridWidgets.filter((_, i) => i % 3 === 1).map(w => (
                <SortableWidget key={w.id} id={w.id} onClose={hideWidget}>
                  {renderWidgetContent(w.id)}
                </SortableWidget>
              ))}
            </div>

            {/* Column 2 — widgets at positions 2, 5, 8, … + static cards */}
            <div className="flex flex-col gap-6">
              {visibleGridWidgets.filter((_, i) => i % 3 === 2).map(w => (
                <SortableWidget key={w.id} id={w.id} onClose={hideWidget}>
                  {renderWidgetContent(w.id)}
                </SortableWidget>
              ))}
              {/* Static: Word Strength */}
              <Card data-testid="word-strength-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-primary"/><span className="font-semibold">{t('dashboard.wordStrength')}</span></div>
                  <div className="text-center py-4"><div className="text-4xl font-bold text-green-500">{insights?.average_strength||0}%</div><p className="text-sm text-muted-foreground mt-1">{t('stats.avgStrength')}</p></div>
                  <div className="flex justify-center gap-1 mt-2">{[t('dashboard.weak'),t('dashboard.medium'),t('dashboard.strong'),t('dashboard.mastered')].map((label,idx)=>(<div key={label} className={`h-2 flex-1 rounded-full ${idx===0?'bg-red-500/30':idx===1?'bg-orange-500/30':idx===2?'bg-yellow-500/30':'bg-green-500/30'}`}>{insights?.average_strength>=(idx+1)*25&&<div className={`h-full rounded-full ${idx===0?'bg-red-500':idx===1?'bg-orange-500':idx===2?'bg-yellow-500':'bg-green-500'}`}/>}</div>))}</div>
                </CardContent>
              </Card>
              {/* Static: Learning Insight */}
              <Card data-testid="learning-insight-card">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4"><Lightbulb className="h-5 w-5 text-yellow-500"/><span className="font-semibold">{t('dashboard.learningInsight')}</span></div>
                  <p className="text-muted-foreground">{insights?.months_to_goal?(<>{t('dashboard.keepLearning')} <span className="font-semibold text-foreground">{insights.target_words?.toLocaleString()}</span> {t('common.words')} {' '}<span className="font-semibold text-primary">{insights.months_to_goal} {t('common.months')}</span>.</>):(<>{t('dashboard.noInsightsYet')} {t('dashboard.keepLearning')}</>)}</p>
                  {insights?.words_per_day>0&&<div className="mt-4 p-3 rounded-lg bg-muted/50"><div className="flex items-center justify-between text-sm"><span className="text-muted-foreground">{t('common.wordsPerDay')}</span><span className="font-semibold">{insights.words_per_day}</span></div></div>}
                </CardContent>
              </Card>
            </div>
          </div>
        </SortableContext>
      </DndContext>

      {/* Language Texts Selection Modal */}
      <Dialog open={languageTextsModal.open} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {languageTextsModal.language && (
                  <FlagImage countryCode={getCountryCode(languageTextsModal.language)} className="w-8 h-6" />
                )}
                {languageTextsModal.language?.name} Texts
              </div>
              {/* Proficiency Badge - Click to change */}
              {languageTextsModal.language && (
                <button
                  onClick={() => handleOpenProficiencyModal(
                    languageTextsModal.language, 
                    languageTextsModal.language.proficiency
                  )}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors hover:opacity-80 ${
                    languageTextsModal.language.proficiency === 'advanced' 
                      ? 'bg-red-500/10 border-red-500/30 text-red-500' 
                      : languageTextsModal.language.proficiency === 'intermediate'
                        ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-600'
                        : 'bg-green-500/10 border-green-500/30 text-green-500'
                  }`}
                  title="Click to change level"
                  data-testid="change-proficiency-btn"
                >
                  {languageTextsModal.language.proficiency || 'beginner'}
                </button>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* My Texts / Public Texts Tabs */}
            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg" data-testid="texts-tab-switcher">
              <button
                onClick={() => setTextsTab('mine')}
                className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  textsTab === 'mine' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-my-texts"
              >
                My Texts ({languageTexts.length})
              </button>
              <button
                onClick={() => setTextsTab('public')}
                className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  textsTab === 'public' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="tab-public-texts"
              >
                Public ({publicTexts.length})
              </button>
            </div>

            {/* Text sorting and category filter for this language */}
            {((textsTab === 'mine' && languageTexts.length > 0) || (textsTab === 'public' && publicTexts.length > 0)) && (
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={textSortBy}
                  onChange={(e) => setTextSortBy(e.target.value)}
                  className="text-xs bg-muted/50 border border-border rounded px-2 py-1"
                  data-testid="text-sort-select"
                >
                  <option value="recent">Most recent</option>
                  <option value="title">Title A-Z</option>
                  <option value="progress">Progress</option>
                </select>
                <select
                  value={textCategoryFilter}
                  onChange={(e) => setTextCategoryFilter(e.target.value)}
                  className="text-xs bg-muted/50 border border-border rounded px-2 py-1"
                  data-testid="text-category-filter"
                >
                  <option value="">All categories</option>
                  <option value="story">Story</option>
                  <option value="news">News</option>
                  <option value="lesson">Lesson</option>
                  <option value="article">Article</option>
                  <option value="song">Song</option>
                  <option value="dialogue">Dialogue</option>
                  <option value="scripture">Scripture</option>
                  <option value="poem">Poem</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {/* Generate New Text Button - Only for Admin/Tier 3 */}
            {canGenerateTexts && !showGenerateOptions && (
              <Button
                className="w-full gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                onClick={() => setShowGenerateOptions(true)}
                disabled={generatingText}
                data-testid="generate-text-btn"
              >
                <Wand2 className="h-4 w-4" />
                Create New Text (AI Generated)
              </Button>
            )}
            
            {/* Generate Options */}
            {canGenerateTexts && showGenerateOptions && (
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <p className="text-sm font-medium">What type of text would you like?</p>
                
                {!generateMode && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      className="flex-col h-auto py-4 gap-2 relative"
                      onClick={() => setGenerateMode('weak')}
                      data-testid="generate-weak-btn"
                    >
                      <CreditBadge cost={CREDIT_COSTS.VOCABULARY_TEXT} className="absolute top-2 right-2" />
                      <Sparkles className="h-5 w-5 text-amber-500" />
                      <span className="text-sm">Weak Words</span>
                      <span className="text-xs text-muted-foreground">Practice words you're learning</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-col h-auto py-4 gap-2 relative"
                      onClick={() => setGenerateMode('topic')}
                      data-testid="generate-topic-btn"
                    >
                      <CreditBadge cost={CREDIT_COSTS.TOPIC_GENERATION} className="absolute top-2 right-2" />
                      <Lightbulb className="h-5 w-5 text-blue-500" />
                      <span className="text-sm">Specific Topic</span>
                      <span className="text-xs text-muted-foreground">Choose what to read about</span>
                    </Button>
                  </div>
                )}
                
                {generateMode === 'weak' && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Generate a text using words you're still learning (strength &lt; 70%)
                    </p>
                    <div className="flex items-center gap-2 text-amber-500 text-sm">
                      <Coins className="h-4 w-4" />
                      <span>This will cost {CREDIT_COSTS.VOCABULARY_TEXT} credits</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleGenerateText('weak')}
                        disabled={generatingText}
                        className="flex-1 gap-2"
                      >
                        {generatingText ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                        ) : (
                          <><Wand2 className="h-4 w-4" /> Generate</>
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setGenerateMode(null)}>Back</Button>
                    </div>
                  </div>
                )}
                
                {generateMode === 'topic' && (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="topic-input" className="text-sm">Enter a topic or instruction</Label>
                      <Input
                        id="topic-input"
                        placeholder="e.g., simple conversation at A1 level, travel story..."
                        value={topicInput}
                        onChange={(e) => { if (e.target.value.length <= 200) setTopicInput(e.target.value); }}
                        className="mt-1"
                        data-testid="topic-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {topicInput.length}/200
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-amber-500 text-sm">
                      <Coins className="h-4 w-4" />
                      <span>This will cost {CREDIT_COSTS.TOPIC_GENERATION} credits</span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleGenerateText('topic')}
                        disabled={generatingText || !topicInput.trim()}
                        className="flex-1 gap-2"
                      >
                        {generatingText ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                        ) : (
                          <><Wand2 className="h-4 w-4" /> Generate</>
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setGenerateMode(null)}>Back</Button>
                    </div>
                  </div>
                )}
                
                {!generateMode && (
                  <Button variant="ghost" size="sm" onClick={() => setShowGenerateOptions(false)} className="w-full">
                    Cancel
                  </Button>
                )}
              </div>
            )}
            
            {/* Texts List */}
            <ScrollArea className="h-[300px] pr-4">
              {loadingLanguageTexts ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : (textsTab === 'mine' ? languageTexts : publicTexts).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{textsTab === 'mine' ? 'No texts for this language yet' : 'No public texts available'}</p>
                  {textsTab === 'mine' && (
                    <Link to="/import">
                      <Button variant="outline" className="mt-4 gap-2">
                        <Plus className="h-4 w-4" />
                        Import a text
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {textsTab === 'public' ? (
                    // Group public texts by series (with filter + sort applied)
                    (() => {
                      const filtered = [...publicTexts]
                        .filter(t => !textCategoryFilter || t.category === textCategoryFilter)
                        .sort((a, b) => {
                          if (textSortBy === 'title') return (a.title || '').localeCompare(b.title || '');
                          if (textSortBy === 'progress') {
                            const pa = a.known_percent ?? (a.unique_words ? (a.known_words || 0) / a.unique_words * 100 : 0);
                            const pb = b.known_percent ?? (b.unique_words ? (b.known_words || 0) / b.unique_words * 100 : 0);
                            return pb - pa;
                          }
                          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                        });
                      const seriesMap = {};
                      const standalone = [];
                      filtered.forEach(text => {
                        if (text.series_name) {
                          if (!seriesMap[text.series_name]) seriesMap[text.series_name] = [];
                          seriesMap[text.series_name].push(text);
                        } else {
                          standalone.push(text);
                        }
                      });
                      // Sort within each series by series_order
                      Object.values(seriesMap).forEach(arr => arr.sort((a, b) => (a.series_order || 0) - (b.series_order || 0)));
                      
                      return (
                        <>
                          {Object.entries(seriesMap).map(([seriesName, texts]) => (
                            <div key={seriesName} className="mb-3">
                              <div className="flex items-center gap-2 px-1 mb-1.5">
                                <Library className="h-3.5 w-3.5 text-primary" />
                                <span className="text-xs font-semibold uppercase tracking-wider text-primary">{seriesName}</span>
                                <span className="text-xs text-muted-foreground">({texts.length})</span>
                              </div>
                              <div className="space-y-1 pl-1 border-l-2 border-primary/20 ml-1.5">
                                {texts.map((text, idx) => {
                                  const wordCount = text.word_count || text.total_words || 0;
                                  const progress = text.known_percent ?? 0;
                                  return (
                                    <Link key={text.text_id} to={`/reader/${text.text_id}`}
                                      onClick={() => setLanguageTextsModal({ open: false, language: null })}>
                                      <div className="p-2.5 pl-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-mono text-muted-foreground w-5">{idx + 1}.</span>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm break-words" dir="auto">{text.title}</p>
                                            <div className="flex items-center justify-between gap-1 text-xs text-muted-foreground mt-0.5">
                                              <div className="flex items-center gap-2">
                                                <span>{wordCount} words</span>
                                                <span>•</span>
                                                <span>{Math.round(progress)}% known</span>
                                              </div>
                                              {fmtDate(text.created_at) && (
                                                <span className="opacity-50">{fmtDate(text.created_at)}</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <Progress value={progress} className="h-1 mt-1.5" />
                                      </div>
                                    </Link>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                          {standalone.length > 0 && Object.keys(seriesMap).length > 0 && (
                            <div className="flex items-center gap-2 px-1 mb-1.5 mt-3">
                              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Individual Texts</span>
                            </div>
                          )}
                          {standalone.map(text => {
                            const wordCount = text.word_count || text.total_words || 0;
                            const uniqueWords = text.unique_words || wordCount;
                            const knownWords = text.known_words || 0;
                            const progress = text.known_percent != null 
                              ? text.known_percent 
                              : (uniqueWords > 0 ? (knownWords / uniqueWords) * 100 : 0);
                            return (
                              <Link key={text.text_id} to={`/reader/${text.text_id}`}
                                onClick={() => setLanguageTextsModal({ open: false, language: null })}>
                                <div className="p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
                                  <div className="flex items-center gap-3">
                                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm break-words" dir="auto">{text.title}</p>
                                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                        <span>{wordCount.toLocaleString()} words</span>
                                        <span>•</span>
                                        <span>{Math.round(progress)}% known</span>
                                        {text.unknown_words != null && (
                                          <>
                                            <span>•</span>
                                            <span className="text-amber-500">{text.unknown_words} unknown</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    {fmtDate(text.created_at) && (
                                      <span className="text-xs text-muted-foreground opacity-50 flex-shrink-0 self-end">{fmtDate(text.created_at)}</span>
                                    )}
                                  </div>
                                  <Progress value={progress} className="h-1.5 mt-2" />
                                </div>
                              </Link>
                            );
                          })}
                        </>
                      );
                    })()
                  ) : (
                    // My Texts - sorted flat list (#47), category filtered (#36-37)
                    [...languageTexts]
                    .filter(t => !textCategoryFilter || t.category === textCategoryFilter)
                    .sort((a, b) => {
                      if (textSortBy === 'title') return (a.title || '').localeCompare(b.title || '');
                      if (textSortBy === 'progress') {
                        const pa = a.known_percent ?? (a.unique_words ? (a.known_words || 0) / a.unique_words * 100 : 0);
                        const pb = b.known_percent ?? (b.unique_words ? (b.known_words || 0) / b.unique_words * 100 : 0);
                        return pb - pa;
                      }
                      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                    }).map(text => {
                    const wordCount = text.word_count || text.total_words || 0;
                    const uniqueWords = text.unique_words || wordCount;
                    const knownWords = text.known_words || 0;
                    const progress = text.known_percent != null 
                      ? text.known_percent 
                      : (uniqueWords > 0 ? (knownWords / uniqueWords) * 100 : 0);
                    
                    return (
                      <Link 
                        key={text.text_id} 
                        to={`/reader/${text.text_id}`}
                        onClick={() => setLanguageTextsModal({ open: false, language: null })}
                      >
                        <div className="p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm break-words ${/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(text.title) ? 'font-arabic' : ''}`} dir="auto">{text.title}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                <span>{wordCount.toLocaleString()} words</span>
                                <span>·</span>
                                <span>{Math.round(progress)}% known</span>
                                {text.category && (
                                  <>
                                    <span>·</span>
                                    <span className="text-primary/70 capitalize">{text.category}</span>
                                  </>
                                )}
                                {text.is_generated && (
                                  <>
                                    <span>•</span>
                                    <span className="text-purple-500">AI Generated</span>
                                  </>
                                )}
                                {textsTab === 'public' && text.unknown_words != null && (
                                  <>
                                    <span>•</span>
                                    <span className="text-amber-500">{text.unknown_words} unknown</span>
                                  </>
                                )}
                              </div>
                            </div>
                            {fmtDate(text.created_at) && (
                              <span className="text-xs text-muted-foreground opacity-50 flex-shrink-0 self-end">{fmtDate(text.created_at)}</span>
                            )}
                          </div>
                          <Progress value={progress} className="h-1.5 mt-2" />
                        </div>
                      </Link>
                    );
                  }))
                  }
                </div>
              )}
            </ScrollArea>
            
            {/* Import New Link - Only for Tier 2+ */}
            <div className="pt-2 border-t">
              {canImport ? (
                <Link to="/import" onClick={() => setLanguageTextsModal({ open: false, language: null })}>
                  <Button variant="outline" className="w-full gap-2">
                    <Plus className="h-4 w-4" />
                    Import New Text
                  </Button>
                </Link>
              ) : (
                <Button variant="outline" className="w-full gap-2 opacity-50" disabled>
                  <Lock className="h-4 w-4" />
                  Import (Tier 2+)
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Batch Generate Modal */}
      <Dialog open={batchGenerateModal} onOpenChange={(open) => !open && handleCloseBatchModal()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-500" />
              Generate Texts for All Languages
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Enter a topic and we'll generate a ~250 word text for each of your {languageStats.length} language(s).
              The vocabulary difficulty will be adjusted based on your proficiency level in each language.
            </p>
            
            {/* Proficiency info */}
            <div className="p-3 rounded-lg bg-muted/50 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Vocabulary by Proficiency</p>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="flex flex-col items-center p-2 rounded bg-green-500/10">
                  <span className="font-medium text-green-600">Beginner</span>
                  <span className="text-muted-foreground text-center">Simple, everyday words</span>
                </div>
                <div className="flex flex-col items-center p-2 rounded bg-yellow-500/10">
                  <span className="font-medium text-yellow-600">Intermediate</span>
                  <span className="text-muted-foreground text-center">1500-3000 frequency</span>
                </div>
                <div className="flex flex-col items-center p-2 rounded bg-red-500/10">
                  <span className="font-medium text-red-600">Advanced</span>
                  <span className="text-muted-foreground text-center">Formal, 20% rare words</span>
                </div>
              </div>
            </div>
            
            {/* Languages that will be generated */}
            <div>
              <p className="text-sm font-medium mb-2">Languages ({languageStats.length}):</p>
              <div className="flex flex-wrap gap-2">
                {languageStats.map((ls, idx) => {
                  const lang = ls.language;
                  const proficiency = lang?.proficiency || 'beginner';
                  const profColor = proficiency === 'advanced' ? 'red' : proficiency === 'intermediate' ? 'yellow' : 'green';
                  return (
                    <div 
                      key={lang?.language_id || idx} 
                      className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-${profColor}-500/10 border border-${profColor}-500/30`}
                    >
                      <FlagImage countryCode={getCountryCode(lang)} className="w-4 h-3" />
                      <span>{lang?.name}</span>
                      <span className={`text-${profColor}-600 font-medium`}>({proficiency})</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Topic input */}
            {!batchResults && (
              <div className="space-y-2">
                <Label htmlFor="batch-topic" className="text-sm font-medium">Topic or instruction</Label>
                <Input
                  id="batch-topic"
                  placeholder="e.g., simple dialogue at A1 level, daily routines..."
                  value={batchTopicInput}
                  onChange={(e) => { if (e.target.value.length <= 200) setBatchTopicInput(e.target.value); }}
                  disabled={batchGenerating}
                  data-testid="batch-topic-input"
                />
                <p className="text-xs text-muted-foreground">
                  {batchTopicInput.length}/200
                </p>
              </div>
            )}
            
            {/* Results */}
            {batchResults && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-green-600">
                  Generated {batchResults.total_generated} text(s) about "{batchResults.topic}"
                </p>
                <ScrollArea className="h-48 rounded border p-2">
                  {batchResults.generated_texts?.map((text, idx) => (
                    <Link 
                      key={text.text_id} 
                      to={`/reader/${text.text_id}`}
                      onClick={handleCloseBatchModal}
                      className="block p-2 rounded hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FlagImage countryCode={getCountryCode({ code: text.language_code })} className="w-5 h-4" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{text.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {text.language_name} • {text.word_count} words • {text.proficiency}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                  {batchResults.errors?.map((err, idx) => (
                    <div key={idx} className="p-2 text-sm text-red-500">
                      Failed: {err.language_name || err.language_id} - {err.error}
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-2 pt-2">
              {!batchResults ? (
                <>
                  <Button
                    onClick={handleBatchGenerate}
                    disabled={batchGenerating || !batchTopicInput.trim()}
                    className="flex-1 gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    data-testid="batch-generate-submit"
                  >
                    {batchGenerating ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Generating {languageStats.length} texts...</>
                    ) : (
                      <><Wand2 className="h-4 w-4" /> Generate</>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleCloseBatchModal}>Cancel</Button>
                </>
              ) : (
                <Button variant="outline" className="w-full" onClick={handleCloseBatchModal}>Close</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Lesson Modal */}
      <Dialog open={showNewLessonModal} onOpenChange={setShowNewLessonModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-500" />
              Create New Lesson
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-2">
            {/* 1. Select Topic */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">1. Choose a Topic</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {LESSON_TOPICS.map(topic => (
                  <button
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic.id)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      selectedTopic === topic.id
                        ? 'border-indigo-500 bg-indigo-500/10 ring-1 ring-indigo-500/50'
                        : 'hover:border-indigo-500/50 hover:bg-muted/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">{topic.icon}</div>
                    <div className="font-medium text-sm">{topic.label}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* 2. Select ILR Level */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">2. Target Level (Optional)</Label>
              <Select value={lessonILR} onValueChange={setLessonILR}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select difficulty..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (Use my current level)</SelectItem>
                  <SelectItem value="1">ILR Level 1 - Elementary / Beginner</SelectItem>
                  <SelectItem value="2">ILR Level 2 - Limited Working / Intermediate</SelectItem>
                  <SelectItem value="3">ILR Level 3 - General Professional / Advanced</SelectItem>
                  <SelectItem value="4">ILR Level 4 - Advanced Professional / Superior</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                "Auto" will use the proficiency level set for each language.
              </p>
            </div>

            {/* 3. Select Languages */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">3. Select Languages</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="xs" 
                    onClick={() => setSelectedLessonLanguages(languageStats.map(ls => ls.language.language_id))}
                    className="h-6 text-xs"
                  >
                    Select All
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="xs" 
                    onClick={() => setSelectedLessonLanguages([])}
                    className="h-6 text-xs"
                  >
                    Clear
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {languageStats.map(ls => {
                  const lang = ls.language;
                  const isSelected = selectedLessonLanguages.includes(lang.language_id);
                  return (
                    <button
                      key={lang.language_id}
                      onClick={() => toggleLessonLanguage(lang.language_id)}
                      className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-500/10'
                          : 'hover:bg-muted'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-muted-foreground'
                      }`}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </div>
                      <FlagImage countryCode={getCountryCode(lang)} className="w-5 h-4" />
                      <span className="text-sm font-medium truncate">{lang.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <Button 
              className="w-full h-12 text-lg gap-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 border-0 shadow-lg"
              onClick={handleGenerateNewLesson}
              disabled={generatingLesson || !selectedTopic || selectedLessonLanguages.length === 0}
            >
              {generatingLesson ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Generating Lesson...
                </>
              ) : (
                <>
                  <Wand2 className="h-5 w-5" />
                  Generate Lesson
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Proficiency Change Modal */}
      <Dialog open={proficiencyModal.open} onOpenChange={(open) => !open && setProficiencyModal({ open: false, language: null, currentProficiency: 'beginner' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {proficiencyModal.language && (
                <FlagImage countryCode={getCountryCode(proficiencyModal.language)} className="w-8 h-6" />
              )}
              Change {proficiencyModal.language?.name} Level
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Disclaimer */}
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm">
              <p className="font-medium text-amber-600 mb-2">How to gauge your level:</p>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li><span className="text-green-500 font-medium">Beginner:</span> Know fewer than 1,500 words from the frequency list</li>
                <li><span className="text-yellow-500 font-medium">Intermediate:</span> Know between 1,500–3,000 words from the frequency list</li>
                <li><span className="text-red-500 font-medium">Advanced:</span> Know more than 4,000 words from the frequency list</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2 italic">
                *Not counting derivatives, conjugations, or declinations
              </p>
            </div>
            
            {/* Level Selection */}
            <div className="space-y-2">
              <p className="text-sm font-medium">Select your level:</p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={proficiencyModal.currentProficiency === 'beginner' ? 'default' : 'outline'}
                  className={`flex-col h-auto py-4 gap-1 ${proficiencyModal.currentProficiency === 'beginner' ? 'bg-green-500 hover:bg-green-600' : 'hover:bg-green-500/10 hover:border-green-500/50'}`}
                  onClick={() => handleUpdateProficiency('beginner')}
                  disabled={updatingProficiency}
                  data-testid="proficiency-beginner"
                >
                  <span className="text-sm font-semibold">Beginner</span>
                  <span className="text-xs opacity-70">&lt;1,500 words</span>
                </Button>
                <Button
                  variant={proficiencyModal.currentProficiency === 'intermediate' ? 'default' : 'outline'}
                  className={`flex-col h-auto py-4 gap-1 ${proficiencyModal.currentProficiency === 'intermediate' ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : 'hover:bg-yellow-500/10 hover:border-yellow-500/50'}`}
                  onClick={() => handleUpdateProficiency('intermediate')}
                  disabled={updatingProficiency}
                  data-testid="proficiency-intermediate"
                >
                  <span className="text-sm font-semibold">Intermediate</span>
                  <span className="text-xs opacity-70">1,500–3,000</span>
                </Button>
                <Button
                  variant={proficiencyModal.currentProficiency === 'advanced' ? 'default' : 'outline'}
                  className={`flex-col h-auto py-4 gap-1 ${proficiencyModal.currentProficiency === 'advanced' ? 'bg-red-500 hover:bg-red-600' : 'hover:bg-red-500/10 hover:border-red-500/50'}`}
                  onClick={() => handleUpdateProficiency('advanced')}
                  disabled={updatingProficiency}
                  data-testid="proficiency-advanced"
                >
                  <span className="text-sm font-semibold">Advanced</span>
                  <span className="text-xs opacity-70">&gt;4,000 words</span>
                </Button>
              </div>
            </div>
            
            {updatingProficiency && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Language Removal Confirmation Modal */}
      <Dialog open={removeModal.open} onOpenChange={(open) => !open && setRemoveModal({ open: false, language: null, step: 'confirm' })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {removeModal.language && (
                <FlagImage countryCode={getCountryCode(removeModal.language)} className="w-8 h-6" />
              )}
              Remove {removeModal.language?.name}?
            </DialogTitle>
          </DialogHeader>
          
          {removeModal.step === 'confirm' ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to remove <strong>{removeModal.language?.name}</strong> from your dashboard?
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setRemoveModal(prev => ({...prev, open: false}))}>
                  Cancel
                </Button>
                <Button variant="destructive" size="sm" onClick={() => setRemoveModal(prev => ({...prev, step: 'progress'}))}>
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Do you also want to <strong>delete all progress</strong> (word states, review history) for {removeModal.language?.name}?
              </p>
              <p className="text-xs text-amber-500">
                If you keep your progress, you can restore {removeModal.language?.name} later with all data intact.
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  onClick={() => confirmRemoveLanguage(false)}
                  disabled={removingLanguage}
                  data-testid="remove-lang-keep-progress"
                >
                  {removingLanguage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                  Hide — keep my progress
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  className="justify-start"
                  onClick={() => confirmRemoveLanguage(true)}
                  disabled={removingLanguage}
                  data-testid="remove-lang-delete-progress"
                >
                  {removingLanguage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <X className="h-4 w-4 mr-2" />}
                  Delete — remove all data
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AddLanguageDialog
        open={addLanguageModal}
        onOpenChange={setAddLanguageModal}
        languages={allAvailableLanguages}
        activeIds={languageStats.map(entry => entry.language?.language_id)}
        hiddenIds={hiddenLanguages.map(entry => entry.language_id)}
        loading={loadingAvailableLanguages}
        error={availableLanguagesError}
        busy={addingLanguage}
        onRetry={openAddLanguageModal}
        onAdd={handleAddLanguage}
        onRestore={async id => {
          setAddingLanguage(true);
          if (await handleRestoreLanguage(id)) setAddLanguageModal(false);
          setAddingLanguage(false);
        }}
      />
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-4 w-48 mt-2" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="pt-5 pb-4">
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <Card><CardContent className="pt-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
          <Card><CardContent className="pt-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
        </div>
        <div className="space-y-6">
          <Card><CardContent className="pt-6"><Skeleton className="h-64 w-full" /></CardContent></Card>
        </div>
        <div className="space-y-6">
          <Card><CardContent className="pt-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
          <Card><CardContent className="pt-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
        </div>
      </div>
    </div>
  );
}
